import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { CreateProduct, Product } from '../models/product';
import {
  IngredientWithProduct,
  NewProductToComponents,
} from '../models/ingredient';
import { SearchProductQuery } from '../state/products/products.actions';
import { CreateUpdateProduct } from '../modal/create-update-product/types';
import { UnitOfMeasure } from '../models/enums';
import { CreateDishWithLinkRequest } from './models/create-dish-with-link';

export type LinkProductToIngredientBody = {
  componentId: string,
  quantity: number,
  unit: UnitOfMeasure,
};

export type UnlinkProductFromIngredientBody = {
  componentId: string,
};

@Injectable({
  providedIn: 'root'
})
export class ProductsApi {
  private http = inject(HttpClient);

  searchProduct(userId: string, query: SearchProductQuery) {
    const { s, own, supplierIds, ingredientId } = query;

    const params: Record<string, any> = {};
    if (s) {
      params['s'] = s;
    }

    if (!(own === null || own === undefined)) {
      params['own'] = own;
    }

    if (supplierIds?.length) {
      params['supplierIds'] = supplierIds.join(',');
    }

    if (ingredientId) {
      params['ingredientId'] = ingredientId;
    }

    return this.http
      .get<Product[]>(
        `${ environment.apiUrl }/user/${ userId }/products`,
        { params }
      );
  }

  linkProductToIngredient(
    productId: number,
    userId: string,
    body: LinkProductToIngredientBody,
  ) {
    return this.http
      .post<IngredientWithProduct>(
        `${ environment.apiUrl }/user/${ userId }/products/${ productId }/link-to-ingredient`,
        body,
      );
  }

  createProductWithLinkToIngredient(
    userId: string,
    body: CreateDishWithLinkRequest,
  ) {
    return this.http
      .post<IngredientWithProduct>(
        `${ environment.apiUrl }/user/${ userId }/products/link-to-ingredient`,
        body,
      );
  }

  updateLinkProductToIngredient(
    productId: number,
    userId: string,
    body: Partial<LinkProductToIngredientBody>,
  ) {
    return this.http
      .patch<IngredientWithProduct>(
        `${ environment.apiUrl }/user/${ userId }/products/${ productId }/link-to-ingredient`,
        body,
      );
  }

  unlinkProductFromIngredient(
    productId: number,
    userId: string,
    body: UnlinkProductFromIngredientBody,
  ) {
    return this.http
      .post<{ id: number, productId: number }>(
        `${ environment.apiUrl }/user/${userId}/products/${productId}/unlink-from-ingredient`,
        body
      );
  }

  makeLinkActive(
    productId: number,
    userId: string,
    componentId: string,
  ) {
    return this.http
      .post<NewProductToComponents>(
        `${ environment.apiUrl }/user/${userId}/products/${productId}/ingredients/${componentId}/activate-link`,
        {}
      );
  }

  createProduct(
    product: CreateProduct,
    userId: string,
  ) {
    return this.http
      .post<NewProductToComponents>(
        `${ environment.apiUrl }/user/${userId}/products`,
        product
      );
  }

  updateProduct(
    product: Partial<CreateUpdateProduct>,
    userId: string,
  ) {
    const { id, ...rest } = product;
    return this.http
      .patch<NewProductToComponents>(
        `${ environment.apiUrl }/user/${userId}/products/${id}`,
        rest
      );
  }
}
